// BUILD-046 Owner direction 2026-10-24 (OWNER-APPROVED - BUILDER RECONCILIATION REQUIRED): "Edit task should let the requester manage the assignment they created" - the Customer / Job (add, change, remove)
// and the collaborators (add, remove) - with the searchable controls the Assign dialog already uses (JobField, PeopleCombobox), and clear access to the EXISTING Reassign and Add recipients operations.
// Pass F1 (Owner direction 2026-10-25): these are now three CELLS of the compact, full-width Edit task form (Customer / Job beside the description; Assigned to and Collaborators on the last row), not a nested
// "Assignment" block. Explanations sit behind small info buttons; a restriction the person can act on is written beside its control.
//
// BUILD-046 customer association (Builder authority 229bdaa3): a Customer cell sits BESIDE the Customer / Job cell, with its own control (CustomerField) and its own props. When customers are on
// (`customersEnabled`) the Customer is the control that is drawn, and the JOB is never offered here: a task that already has a job shows it read only ("Job: X", no clear or remove control), so a
// customer edit can never change or erase it. When customers are off the Customer / Job cell is exactly what it was.
//
// This decides nothing: the server judges every change (the requester's alone for the job, the customer and the collaborators, the existing rules for Reassign and Add recipients), and a refusal is shown in the server's
// words by the host. Reassign and Add recipients are not re-implemented here: the buttons ask the row that owns them to open ITS flow (assignmentActionStore.ts), and only when the existing rules offer it.
// Adding people to an existing Single or Anyone request, and changing a request between Single, Anyone and Everyone, are not supported by the server yet; an info button says so instead of pretending.
import { workApi } from '../../api/workShared';
import type { WorkEmployeeRef } from '../../api/workShared';
import type { JobChoice } from './jobFieldModel';
import { JobField } from './JobField';
import type { CustomerChoice } from './customerFieldModel';
import { CustomerField } from './CustomerField';
import { PeopleCombobox } from './PeopleCombobox';
import { describeList } from './listStateModel';
import { useOptionList } from './AssignTaskDialog';
import { InfoButton } from './InfoButton';
import { PersonName } from './PersonName';
import { initialsOf } from './requesterContextModel';

const loadPeople = () => workApi.listEmployees().then((r) => r.employees);
const searchJobs = (q: string) => workApi.searchJobs(q);
const searchCustomers = (q: string) => workApi.searchCustomers(q);

/** What the cells need to know about the task. All of it is read from the task the form was opened on. */
export interface EditAssignmentProps {
  callerEmployeeId: string | null;
  /** `GET /work/me` canSearchJobs: without it the job control is not drawn (nothing could be chosen). It is also not drawn while `customersEnabled` is true: a job is then only shown. */
  jobsEnabled: boolean;
  /** Why the job cannot be changed on this task (an Everyone recipient's task belongs to the request's job), or null. */
  jobLockedReason: string | null;
  /** `GET /work/me` canSearchCustomers: without it the customer control is not drawn (nothing could be chosen). */
  customersEnabled: boolean;
  /** Why the customer cannot be changed on this task (an Everyone recipient's task belongs to the request's customer), or null. */
  customerLockedReason: string | null;
  /** Who has the task now, as a name (already shortened for display), or ''. */
  ownerName: string;
  /** 'direct' (Single), 'shared' (Anyone) or 'everyone'. */
  kind: string;
  /** The Reassign button is drawn only where the existing rules offer it; otherwise the reason is behind an info button. Null: this form is not opened from Assigned by Me, so neither button is drawn. */
  reassign: { offered: boolean; reason: string } | null;
  /** Add recipients: only an Everyone request supports it. Null: not drawn. */
  onReassign: (() => void) | null;
  onAddRecipients: (() => void) | null;
}

export const NOT_SUPPORTED_ADD = 'Adding people to a Single or Anyone request is not supported yet. Add a collaborator here, or assign a separate task. Changing a request between Single, Anyone and Everyone is not supported yet either.';
export const EVERYONE_ADD_NOTE = 'This is an Everyone request: a person you add gets a task of their own that they must complete, and work already done stays. It is never turned into an Anyone request.';
export const JOB_ONLY_REQUESTER_NOTE = 'Only the person who issued the task can change its Customer / Job.';
/** The original name of the job control, and the one this note keeps whenever the cell is not drawn beside the Customer cell. */
const ORIGINAL_JOB_WORDS = 'Customer / Job';
export const CUSTOMER_ONLY_REQUESTER_NOTE = 'Only the person who issued the task can change its Customer.';
/** Why the job cannot be changed while the Customer is the control of this form. */
export const JOB_KEPT_NOTE = 'The job on this task is kept as it is. This form changes the Customer only.';

const LABEL = 'mb-1 flex items-center gap-1 text-[11px] font-medium text-gray-600';
const BUTTON = 'rounded border border-[#2a7fb8] bg-white px-2.5 py-1 text-xs font-medium text-[#1f6fa5] hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] disabled:opacity-50';

/**
 * True when the form draws a Customer / Job cell: the requester's control (where jobs can be searched, or the task is locked to its request), or a task that already has a job.
 * While customers are on (`customersEnabled`) the job is never chosen here, so the cell exists only to SHOW a job the task already has.
 */
export function hasJobCell(assignment: EditAssignmentProps | undefined, job: JobChoice | null): boolean {
  if (assignment === undefined) return job !== null;
  if (assignment.customersEnabled === true) return job !== null;
  return assignment.jobLockedReason !== null || assignment.jobsEnabled || job !== null;
}
/** True when the form draws a Customer cell: the requester's control (where customers can be searched, or the task is locked to its request), or a task that already has a customer. */
export function hasCustomerCell(assignment: EditAssignmentProps | undefined, customer: CustomerChoice | null): boolean {
  if (assignment === undefined) return customer !== null;
  return assignment.customerLockedReason !== null || assignment.customersEnabled || customer !== null;
}

/** The requester's control is drawn only where the task is not locked to its request and jobs can be searched. Everything else that has a cell shows the job read only, with the reason behind an info button. */
function jobReadOnlyReason(assignment: EditAssignmentProps | undefined, words: string): string {
  // CHANGED 2026-10-25: the sentences use the words of the control ("Job" beside the Customer cell); the original "Customer / Job" is kept word for word when the cell is not beside a Customer.
  if (assignment === undefined) return words === ORIGINAL_JOB_WORDS ? JOB_ONLY_REQUESTER_NOTE : `Only the person who issued the task can change its ${words}.`;
  if (assignment.jobLockedReason !== null) return assignment.jobLockedReason;
  if (assignment.customersEnabled === true) return JOB_KEPT_NOTE;
  return `Jobs cannot be searched for you, so the ${words} cannot be changed here.`;
}
/** The requester's control is drawn only where the task is not locked to its request and customers can be searched. Everything else that has a cell shows the customer read only, with the reason behind an info button. */
function customerReadOnlyReason(assignment: EditAssignmentProps | undefined): string {
  if (assignment === undefined) return CUSTOMER_ONLY_REQUESTER_NOTE;
  if (assignment.customerLockedReason !== null) return assignment.customerLockedReason;
  return 'Customers cannot be searched for you, so the Customer cannot be changed here.';
}

export interface JobCellProps {
  idBase: string;
  assignment: EditAssignmentProps | undefined;
  job: JobChoice | null;
  disabled: boolean;
  problem: string | null;
  onJob: (job: JobChoice | null) => void;
  onJobDraft: (text: string) => void;
  /**
   * The words the cell calls the job. Absent: "Job" while customers are on (so the Customer cell and this one never share a name), otherwise "Customer / Job" as it has always been.
   * The host may pass "Job" when the task also carries a customer but the form has no assignment props (a read only form for someone who is not the requester).
   */
  label?: string;
}
export interface CustomerCellProps {
  idBase: string;
  assignment: EditAssignmentProps | undefined;
  customer: CustomerChoice | null;
  disabled: boolean;
  problem: string | null;
  onCustomer: (customer: CustomerChoice | null) => void;
  onCustomerDraft: (text: string) => void;
}

export function JobCell({ idBase, assignment, job, disabled, problem, onJob, onJobDraft, label }: JobCellProps) {
  const jobId = `${idBase}-job`;
  if (!hasJobCell(assignment, job)) return null;
  const customersOn = assignment !== undefined && assignment.customersEnabled === true;
  const words = label !== undefined && label.trim() !== '' ? label : customersOn ? 'Job' : 'Customer / Job';
  // Customers are on: the Customer is the control of this form and the job is only SHOWN ("Job: X"), with no way to clear, remove or change it here, so a customer edit can never erase it.
  if (customersOn && job !== null) {
    return (
      <div data-testid="edit-job-kept">
        <p className="break-words text-xs text-gray-700" title={JOB_KEPT_NOTE}>
          <span className="font-medium text-gray-600">{words}:</span> {job.label}
        </p>
      </div>
    );
  }
  // Not the requester, or an Everyone recipient's task, or jobs cannot be searched: the job is shown read only with the reason behind an info button; where the restriction is actionable (an Everyone
  // recipient's job belongs to the whole request) it is also written beside the job.
  if (assignment === undefined || assignment.jobLockedReason !== null || !assignment.jobsEnabled) {
    return (
      <div data-testid={assignment === undefined ? 'edit-job-readonly' : 'edit-job-locked'}>
        <p className={LABEL}>
          {words}
          <InfoButton label={words} testId="edit-job-info">
            <p>{jobReadOnlyReason(assignment, words)}</p>
          </InfoButton>
        </p>
        <p className="break-words text-sm text-gray-900">{job === null ? 'None' : job.label}</p>
        {assignment !== undefined && assignment.jobLockedReason !== null && <p className="text-xs text-gray-600">Set for the whole request.</p>}
      </div>
    );
  }
  return (
    <div data-testid="edit-job-row">
      <label id={`${jobId}-label`} htmlFor={jobId} className={LABEL}>
        {words} <span className="font-normal text-gray-500">(optional)</span>
      </label>
      <JobField id={jobId} job={job} disabled={disabled} search={searchJobs} onChoose={(chosen) => onJob(chosen)} onClear={() => onJob(null)} onDraftChange={onJobDraft} problem={problem} wideList floating words={words} />
    </div>
  );
}

export function CustomerCell({ idBase, assignment, customer, disabled, problem, onCustomer, onCustomerDraft }: CustomerCellProps) {
  const customerId = `${idBase}-customer`;
  if (!hasCustomerCell(assignment, customer)) return null;
  // Not the requester, or an Everyone recipient's task, or customers cannot be searched: the customer is shown read only with the reason behind an info button; where the restriction is actionable (an Everyone
  // recipient's customer belongs to the whole request) it is also written beside the customer.
  if (assignment === undefined || assignment.customerLockedReason !== null || !assignment.customersEnabled) {
    return (
      <div data-testid={assignment === undefined ? 'edit-customer-readonly' : 'edit-customer-locked'}>
        <p className={LABEL}>
          Customer
          <InfoButton label="Customer" testId="edit-customer-info">
            <p>{customerReadOnlyReason(assignment)}</p>
          </InfoButton>
        </p>
        <p className="break-words text-sm text-gray-900">{customer === null ? 'None' : customer.label}</p>
        {assignment !== undefined && assignment.customerLockedReason !== null && <p className="text-xs text-gray-600">Set for the whole request.</p>}
      </div>
    );
  }
  return (
    <div data-testid="edit-customer-row">
      <label id={`${customerId}-label`} htmlFor={customerId} className={LABEL}>
        Customer <span className="font-normal text-gray-500">(optional)</span>
      </label>
      <CustomerField id={customerId} customer={customer} disabled={disabled} search={searchCustomers} onChoose={(chosen) => onCustomer(chosen)} onClear={() => onCustomer(null)} onDraftChange={onCustomerDraft} problem={problem} wideList floating />
    </div>
  );
}

export interface CollaboratorsCellProps {
  idBase: string;
  callerEmployeeId: string | null;
  collaborators: readonly string[];
  disabled: boolean;
  onCollaborators: (ids: string[]) => void;
}

/** The requester's collaborator control. A collaborator can read the one task and nothing else; the info button says so. */
export function CollaboratorsCell({ idBase, callerEmployeeId, collaborators, disabled, onCollaborators }: CollaboratorsCellProps) {
  const [people, reloadPeople] = useOptionList<WorkEmployeeRef>(loadPeople, true);
  const peopleView = describeList(people, 'people', 'There are no people to choose from.');
  const collabId = `${idBase}-collaborators`;
  return (
    <div data-testid="edit-collaborators-row">
      <label id={`${collabId}-label`} htmlFor={collabId} className={LABEL}>
        Collaborators <span className="font-normal text-gray-500">(optional)</span>
        <InfoButton label="Collaborators" testId="edit-collaborators-info">
          <p>A collaborator can read this one task. It does not let them complete it, change who has it, change who else collaborates, or open anything restricted.</p>
        </InfoButton>
      </label>
      <PeopleCombobox
        id={collabId}
        labelledBy={`${collabId}-label`}
        placeholder="Add collaborators..."
        leadingIcon="search"
        people={peopleView}
        onRetry={reloadPeople}
        selectedIds={[...collaborators]}
        disabled={disabled}
        onAdd={(employeeId) => onCollaborators(collaborators.includes(employeeId) ? [...collaborators] : [...collaborators, employeeId])}
        onRemove={(employeeId) => onCollaborators(collaborators.filter((id) => id !== employeeId))}
        callerEmployeeId={callerEmployeeId}
        floating
      />
    </div>
  );
}

export interface AssignedToCellProps {
  /** The stored full name of the person who has the task now; '' when nobody has taken it. */
  ownerFullName: string;
  assignment: EditAssignmentProps | undefined;
  disabled: boolean;
  onReassign: () => void;
  onAddRecipients: () => void;
}

/** Who has the task (initials and name), with the EXISTING Reassign and Add recipients where this form was opened from Assigned by Me and the rules offer them. */
export function AssignedToCell({ ownerFullName, assignment, disabled, onReassign, onAddRecipients }: AssignedToCellProps) {
  const actions = assignment !== undefined && assignment.reassign !== null;
  return (
    <div data-testid="edit-assigned-to">
      <p className={LABEL}>Assigned to</p>
      <div className="flex min-h-[2.25rem] flex-wrap items-center gap-x-2 gap-y-1">
        {ownerFullName !== '' ? (
          <span className="flex min-w-0 items-center gap-1.5" data-testid="edit-assignee">
            <span aria-hidden="true" className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-slate-600 text-[11px] font-semibold text-white">
              {initialsOf(ownerFullName)}
            </span>
            <span className="min-w-0 truncate text-sm text-gray-900">
              <PersonName person={ownerFullName} />
            </span>
          </span>
        ) : (
          <span className="text-sm text-gray-600" data-testid="edit-assignee">Nobody has taken it yet</span>
        )}
        {actions && assignment.reassign !== null && assignment.reassign.offered && assignment.onReassign !== null && (
          <button type="button" disabled={disabled} onClick={onReassign} data-testid="edit-reassign" className={BUTTON}>
            Reassign
          </button>
        )}
        {actions && assignment.reassign !== null && !assignment.reassign.offered && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-600" data-testid="edit-reassign-hint">
            Reassign not available
            <InfoButton label="Reassign" testId="edit-reassign-info">
              <p>{assignment.reassign.reason}</p>
            </InfoButton>
          </span>
        )}
        {actions && assignment.kind === 'everyone' && assignment.onAddRecipients !== null && (
          <>
            <button type="button" disabled={disabled} onClick={onAddRecipients} data-testid="edit-add-recipients" className={BUTTON}>
              Add recipients
            </button>
            <InfoButton label="Add recipients" testId="edit-add-recipients-info">
              <p data-testid="edit-add-recipients-note">{EVERYONE_ADD_NOTE}</p>
            </InfoButton>
          </>
        )}
        {actions && assignment.kind !== 'everyone' && (
          <InfoButton label="adding people to this request" testId="edit-add-recipients-info">
            <p data-testid="edit-add-recipients-note">{NOT_SUPPORTED_ADD}</p>
          </InfoButton>
        )}
      </div>
    </div>
  );
}
